import { ReactNode, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { classNames } from '../../utils/helpers';
import { SkeletonRows } from './Skeleton';
import EmptyState from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer. */
  render: (row: T) => ReactNode;
  /** Value used for client-side sorting; omit to make the column unsortable. */
  sortValue?: (row: T) => string | number;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Enables a leading checkbox column; reports selected row keys. */
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  /** Client-side pagination page size; omit to disable pagination. */
  pageSize?: number;
  /** Mobile card renderer; falls back to a stacked label/value list. */
  renderMobileCard?: (row: T) => ReactNode;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export default function DataTable<T>({
  columns, data, rowKey, onRowClick, loading,
  emptyTitle = 'Nothing here yet', emptyDescription,
  selectable, selectedKeys = [], onSelectionChange,
  pageSize, renderMobileCard,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!sort) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col?.sortValue) return data;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = col.sortValue!(a), bv = col.sortValue!(b);
      return (av < bv ? -1 : av > bv ? 1 : 0) * factor;
    });
  }, [data, sort, columns]);

  const totalPages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const pageData = pageSize ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  const toggleSort = (key: string) => {
    setSort(s => s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' });
  };

  const allSelected = pageData.length > 0 && pageData.every(r => selectedKeys.includes(rowKey(r)));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const keys = pageData.map(rowKey);
    onSelectionChange(allSelected ? selectedKeys.filter(k => !keys.includes(k)) : [...new Set([...selectedKeys, ...keys])]);
  };
  const toggleOne = (k: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(selectedKeys.includes(k) ? selectedKeys.filter(x => x !== k) : [...selectedKeys, k]);
  };

  if (loading) return <SkeletonRows rows={pageSize ?? 6} cols={columns.length} />;
  if (data.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} />;

  return (
    <div>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              {selectable && (
                <th className="px-4 py-2.5 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />
                </th>
              )}
              {columns.map(col => (
                <th key={col.key} className={classNames('px-4 py-2.5 font-medium whitespace-nowrap', col.className)}>
                  {col.sortValue ? (
                    <button onClick={() => toggleSort(col.key)} className="inline-flex items-center gap-1 hover:text-navy">
                      {col.header}
                      {sort?.key === col.key
                        ? (sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
                        : <ChevronsUpDown size={13} className="text-gray-300" />}
                    </button>
                  ) : col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageData.map(row => {
              const k = rowKey(row);
              return (
                <tr
                  key={k}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={classNames('hover:bg-gray-50 transition-colors', onRowClick && 'cursor-pointer')}
                >
                  {selectable && (
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedKeys.includes(k)} onChange={() => toggleOne(k)} aria-label="Select row" />
                    </td>
                  )}
                  {columns.map(col => (
                    <td key={col.key} className={classNames(
                      'px-4 py-3 text-navy',
                      col.align === 'right' && 'text-right', col.align === 'center' && 'text-center',
                    )}>{col.render(row)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {pageData.map(row => {
          const k = rowKey(row);
          return (
            <div key={k} onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={classNames('py-3', onRowClick && 'cursor-pointer')}>
              {renderMobileCard
                ? renderMobileCard(row)
                : columns.map(col => (
                    <div key={col.key} className="flex justify-between gap-3 py-0.5">
                      <span className="text-xs text-gray-400">{col.header}</span>
                      <span className="text-sm text-navy text-right">{col.render(row)}</span>
                    </div>
                  ))}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pageSize && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          <span>Page {page} of {totalPages} · {sorted.length} items</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
